/**
 * One tenant, as the vendor sees it (phase 7b): status and counts, suspend or
 * reactivate (vendor.admin), and its sites, alarms, live productions and
 * nodes — the ops view narrowed to one tenant.
 */
import type { VendorTenantDetailWire } from '@norna/api-contract/vendor.js';
import { Button } from '../ui/button.js';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card.js';
import { AlarmList } from './AlarmList.js';
import { LiveProductionsTable } from './LiveProductionsTable.js';
import { NodesTable } from './NodesTable.js';
import { hoursText, TenantStatusBadge } from './TenantsTable.js';

export type TenantDetailProps = {
  readonly detail: VendorTenantDetailWire;
  readonly canAdmin: boolean;
  readonly nowMs: number;
  readonly onSuspend: () => void;
  readonly onReactivate: () => void;
};

function Count({ label, value }: { readonly label: string; readonly value: string }) {
  return (
    <div className="grid gap-0.5 rounded-md border px-3 py-2">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="font-mono text-lg">{value}</span>
    </div>
  );
}

export function TenantDetail({ detail, canAdmin, nowMs, onSuspend, onReactivate }: TenantDetailProps) {
  const { tenant } = detail;
  return (
    <div className="grid gap-6" data-testid="tenant-detail">
      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4">
          <div className="grid gap-1.5">
            <CardTitle className="flex items-center gap-2">
              {tenant.name} <TenantStatusBadge tenant={tenant} />
            </CardTitle>
            <CardDescription>
              <span className="font-mono">{tenant.tenantId}</span> · created {new Date(tenant.createdAt).toLocaleDateString()}
              {tenant.status === 'suspended' ? ` · suspended ${tenant.suspendedAt === null ? '' : new Date(tenant.suspendedAt).toLocaleString()}: ${tenant.suspendedReason ?? ''}` : ''}
            </CardDescription>
          </div>
          {canAdmin && !tenant.isVendor && tenant.status !== 'unknown' ? (
            tenant.status === 'active' ? (
              <Button variant="outline" onClick={onSuspend} data-testid="suspend-tenant">
                Suspend
              </Button>
            ) : (
              <Button onClick={onReactivate} data-testid="reactivate-tenant">
                Reactivate
              </Button>
            )
          ) : null}
        </CardHeader>
        <CardContent className="grid grid-cols-2 gap-3 sm:grid-cols-5">
          <Count label="Users" value={String(tenant.counts.users)} />
          <Count label="Sites" value={String(tenant.counts.sites)} />
          <Count label="Nodes" value={String(tenant.counts.nodes)} />
          <Count label="Live productions" value={String(tenant.counts.liveProductions)} />
          <Count label="Cloud hours (month)" value={hoursText(tenant.counts.cloudProductionHoursThisMonth)} />
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Alarms</CardTitle>
        </CardHeader>
        <CardContent>
          <AlarmList alarms={detail.alarms} showTenant={false} />
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Sites</CardTitle>
        </CardHeader>
        <CardContent>
          {detail.sites.length === 0 ? (
            <p className="text-sm text-muted-foreground">No sites.</p>
          ) : (
            <ul className="flex flex-wrap gap-2" data-testid="tenant-sites">
              {detail.sites.map((s) => (
                <li key={s.siteId} className="rounded-md border px-3 py-1 text-sm">
                  {s.name} <span className="font-mono text-xs text-muted-foreground">{s.siteId}</span> · {s.kind}
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Live productions</CardTitle>
        </CardHeader>
        <CardContent>
          <LiveProductionsTable productions={detail.productions} showTenant={false} />
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Nodes</CardTitle>
        </CardHeader>
        <CardContent>
          <NodesTable nodes={detail.nodes} showTenant={false} nowMs={nowMs} />
        </CardContent>
      </Card>
    </div>
  );
}
